import { useMemo } from 'react';
import { Tab } from '../types';
import { SwapType } from '@/domain/swap';
import { Asset, Token, Vault } from 'nftx.js';

export const getInitialTab = (type: SwapType): Tab => {
  switch (type) {
    case 'ETH':
    case 'vTOKEN':
      return 'tokens';
    case 'NFT':
      return 'nfts';
    default:
      return 'nfts';
  }
};

export const usePopularVaults = (
  allVaults: Vault[] | undefined,
  popularVaultIds: string[] | null
) => {
  const popularVaults = useMemo(() => {
    if (!allVaults) {
      return [];
    }
    if (!popularVaultIds || !popularVaultIds.length) {
      // Just return some random vaults
      const randomVaultIndexes = [
        ...new Set(
          new Array(3)
            .fill(null)
            .map(() => Math.floor(Math.random() * allVaults.length))
        ),
      ];
      return randomVaultIndexes.map((i) => allVaults[i]).filter(Boolean);
    }

    return popularVaultIds
      .reduce((acc, vaultId) => {
        const vault = allVaults.find((v) => v.vaultId === vaultId);
        if (!vault) {
          return acc;
        }
        return [...acc, vault];
      }, [] as Vault[])
      .filter(Boolean);
  }, [allVaults, popularVaultIds]);

  return popularVaults;
};

export const useCollections = (
  collectionSet: (Token & { vaultIds: string[]; totalHoldings: number })[],
  search: string,
  searchedVaultIds: string[]
) => {
  const collections = useMemo(() => {
    if (!search) {
      return collectionSet;
    }
    return collectionSet?.filter(({ vaultIds }) =>
      vaultIds.some((vaultId) => searchedVaultIds.includes(vaultId))
    );
  }, [collectionSet, search, searchedVaultIds]);

  return collections;
};

export const useVtokens = (
  tokens: (Token & {
    vaultId: string;
    balance: bigint;
  })[],
  search: string,
  searchedVaultIds: string[],
  mintable = false
) => {
  const vTokens = useMemo(() => {
    const sortedTokens = mintable
      ? tokens.slice().sort((a, b) => {
          if (a.balance > b.balance) {
            return -1;
          }
          if (a.balance < b.balance) {
            return 1;
          }
          return 0;
        })
      : tokens;
    if (!search) {
      return sortedTokens;
    }
    return sortedTokens.filter(({ vaultId }) =>
      searchedVaultIds.includes(vaultId)
    );
  }, [tokens, search, searchedVaultIds, mintable]);

  return vTokens;
};

export const useAssets = ({
  assets: allAssets,
  search,
  searchedVaultIds,
  vaultId,
}: {
  assets: Asset[];
  search: string;
  searchedVaultIds: string[];
  vaultId?: string;
}) => {
  const assets = useMemo(() => {
    let assets = allAssets;

    if (vaultId) {
      assets = assets.filter((asset) => asset.vaultIds.includes(vaultId));
    }

    if (search) {
      assets = assets.filter(({ vaultIds }) =>
        searchedVaultIds.some((vaultId) => vaultIds.includes(vaultId))
      );
    }

    return assets;
  }, [allAssets, search, searchedVaultIds, vaultId]);

  return assets;
};

export const useVisible = ({
  vTokens,
  collections,
  assets,
  assetLimit,
  limit,
}: {
  vTokens: (Token & {
    vaultId: string;
    balance: bigint;
  })[];
  collections: (Token & { vaultIds: string[]; totalHoldings: number })[];
  assets: Asset[];
  assetLimit?: number;
  limit: number;
}) => {
  const visibleVTokens = useMemo(
    () => vTokens.slice(0, limit),
    [limit, vTokens]
  );
  const visibleCollections = useMemo(
    () => collections.slice(0, limit),
    [collections, limit]
  );
  const visibleAssets = useMemo(
    () => assets.slice(0, assetLimit || limit),
    [assetLimit, assets, limit]
  );

  const hasMoreCollections = limit < collections.length;
  const hasMoreTokens = limit < vTokens.length;
  const hasMoreNfts = (assetLimit || limit) < assets.length;

  return {
    visibleVTokens,
    visibleCollections,
    visibleAssets,
    hasMoreCollections,
    hasMoreNfts,
    hasMoreTokens,
  };
};
